import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { clearAuth } from '../../utils/auth';

const Login = () => {
  const nav = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleLogin = async () => {
    if (busy) return;
    setError(null);
    setBusy(true);

    try {
      // Step 1: Get Admin list
      const adminRes = await axios.get(
        `${process.env.REACT_APP_ORIGO_BASE_URL}oauth-jd/admin-list`
      );
      const adminList = adminRes?.data?.data || [];
      const isAdmin = adminList.some(admin => admin.username === username);

      if (isAdmin) {
        // 🔑 Admin flow → only authorize
        const authRes = await axios.post(
          `${process.env.REACT_APP_ORIGO_BASE_URL}oauth-jd/authorize`,
          { username, password, isAdmin: true }
        );

        const auth = authRes?.data?.data || {};
        const access_token = auth.access_token;
        const admin_id = auth.admin_id;

        if (!access_token || !admin_id) {
          throw new Error('Authorization failed – missing token or admin ID');
        }

        localStorage.setItem('origo_access_token', access_token);
        localStorage.setItem('origo_admin_id', admin_id.toString());
        localStorage.setItem('origo_username', username);

        nav('/home');
        return;
      }

      // 👤 Normal user flow → /login first
      localStorage.setItem('origo_username', username);
      localStorage.setItem('origo_password', password);

      const loginRes = await axios.post(
        `${process.env.REACT_APP_ORIGO_BASE_URL}oauth-jd/login`,
        { username, password, restart: false }
      );

      const login = loginRes?.data?.data || {};

      if (login.organizationExists) {
        // ✅ Call /authorize for user
        const authRes = await axios.post(
          `${process.env.REACT_APP_ORIGO_BASE_URL}oauth-jd/authorize`,
          { username, password, isAdmin: false }
        );

        const auth = authRes?.data?.data || {};
        const access_token = auth.access_token;
        const organization_id = auth.organization_id;
        const jd_organization_id = auth.jd_organization_id;

        if (!access_token || !organization_id) {
          throw new Error('Authorization failed – missing token or organization ID');
        }

        localStorage.setItem('origo_access_token', access_token);
        localStorage.setItem('origo_org_id', organization_id.toString());
        localStorage.setItem('jd_organization_id', jd_organization_id ?? '');

        nav('/choose-data');
        return;
      }

      if (login.authorizationUrl) {
        // First-time or reconnect
        localStorage.setItem('origo_auth_url', login.authorizationUrl);
        nav('/home');
        return;
      }

      throw new Error('Unexpected login response – please try again.');
    } catch (e) {
      console.error('Login error', e);
      setError(e?.response?.data?.message || e.message || 'Login failed');
      clearAuth();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen w-full px-4 sm:px-6 lg:px-8 bg-gray-100 dark:bg-gray-900 transition-colors duration-300">
      <div className="flex flex-col md:flex-row bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden max-w-4xl w-full">
        {/* Left Banner */}
        <div
          className="w-full md:w-1/2 h-64 md:h-auto bg-cover bg-right flex flex-col justify-end p-6"
          style={{ backgroundImage: `url(/images/login-img.png)` }}
        >
          <div className="text-white font-poppins space-y-2 text-left">
            <div className="text-xl font-semibold leading-[150%]">Welcome to John Deere</div>
            <div className="text-sm leading-[150%]">
              This portal provides you to connect your Origo application with John Deere and start seeing the sensor data on your John Deere Operations Dashboard.
            </div>
          </div>
        </div>

        {/* Right Form */}
        <div className="w-full md:w-1/2 p-8 flex flex-col">
          <h5 className="text-sm text-gray-700 dark:text-gray-200 mb-4 text-left">Log In</h5>
          <p className="text-xs text-gray-700 dark:text-gray-300 mb-6 text-left">
            Sign in to continue to Origo
          </p>

          <div className="mb-4 w-full mt-6">
            <label className="block text-gray-700 dark:text-gray-200 text-xs font-bold mb-2 text-left">
              Username <span className="text-xs text-red-500">*</span>
            </label>
            <input
              className="text-gray-700 dark:text-white dark:bg-gray-700 dark:border-gray-600 border border-gray-500 rounded-full py-2 px-4 block w-full focus:outline-none focus:ring-2 focus:ring-blue-700 transition"
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>

          <div className="mb-4 w-full">
            <label className="block text-gray-700 dark:text-gray-200 text-xs font-bold mb-2 text-left">
              Password <span className="text-xs text-red-500">*</span>
            </label>
            <input
              className="text-gray-700 dark:text-white dark:bg-gray-700 dark:border-gray-600 border border-gray-500 rounded-full py-2 px-4 block w-full focus:outline-none focus:ring-2 focus:ring-blue-700 transition"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <button
            onClick={handleLogin}
            disabled={busy}
            className="bg-[#2C81C3] rounded-full text-white font-bold py-2 px-4 w-full hover:bg-[#1e6f99] mt-4 disabled:opacity-50 transition"
          >
            {busy ? 'Logging in…' : 'Login'}
          </button>

          {error && (
            <div className="text-red-500 text-xs text-center mt-2">
              {error}
            </div>
          )}

          <div className="text-center mt-6">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Having trouble signing in? Contact {' '}
              <a href="mailto:support@origo.org" className="text-blue-700 dark:text-blue-400 font-medium">
                 support@origo.org
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
